import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { SR_API_ACTION_HOUSEHOLD_NOTE_SHARE } from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_NOTES,
  DATA_TABLE_VIEW_ID_HOUSEHOLD_NOTES_ALL,
} from 'common/dataTableConstants';

import postWithState from 'client/actions/postWithState';

import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrCreateNoteForm, {
  SR_CREATE_NOTE_FORM_THEME_GREYCLIFF,
} from 'client/components/SrCreateNoteForm';
import SrNotesList, {
  SR_HOUSEHOLD_NOTES,
  SR_NOTES_LIST_THEME_GREYCLIFF,
} from 'client/components/SrNotesList';
import SrHouseholdNoteShareModal from 'client/components/SrHouseholdNoteShareModal';

const SrAdminHouseholdNotes = ({
  householdId,
  listingsApplyingFor,
  dispatch,
  currentUser,
  height,
}) => {
  const [shareHouseholdNote, setShareHouseholdNote] = useState(null);

  const _onSubmit = ({ formValues, reloadItems }) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_NOTE_SHARE,
      payload: {
        householdNoteId: shareHouseholdNote.id,
        listingIds: formValues.listingIds,
      },
    }).then(() => {
      reloadItems({
        count: 1,
        filter: { 'householdNotes.id': shareHouseholdNote.id },
      });
      setShareHouseholdNote(null);
    });
  };

  return (
    <SrCard overflow="auto" height={height}>
      <SrText
        font="greycliff"
        fontSize="18px"
        weight="800"
        color="blueGreycliff"
        display="block"
        paddingBottom="20px"
      >
        Household Notes
      </SrText>
      <SrDataTableConnected
        itemsPropName="notes"
        dataSource={DATA_TABLE_SOURCE_HOUSEHOLD_NOTES}
        rootViewId={DATA_TABLE_VIEW_ID_HOUSEHOLD_NOTES_ALL}
        initialViewSettings={{
          sort: {
            createdDate: 'desc',
          },
          filter: {
            householdId: householdId,
          },
        }}
      >
        {({ notes, reloadItems, dataTableApiState, onInfiniteScrollWrapperRef }) => (
          <>
            <SrBox paddingBottom="10px">
              <SrCreateNoteForm
                householdId={householdId}
                dispatch={dispatch}
                onNoteCreated={(note) =>
                  setShareHouseholdNote({
                    ...note,
                    householdSharedListingNotes: [],
                  })
                }
                theme={SR_CREATE_NOTE_FORM_THEME_GREYCLIFF}
                reloadDataTable={(noteId) => {
                  reloadItems({
                    count: 1,
                    filter: { 'householdNotes.id': noteId },
                  });
                }}
              />
            </SrBox>
            <div ref={onInfiniteScrollWrapperRef}>
              <SrNotesList
                type={SR_HOUSEHOLD_NOTES}
                theme={SR_NOTES_LIST_THEME_GREYCLIFF}
                openedInModal
                notes={notes}
                dispatch={dispatch}
                onClickShareNote={setShareHouseholdNote}
                reloadDataTable={(noteId) => {
                  reloadItems({
                    count: 1,
                    filter: { 'householdNotes.id': noteId },
                  });
                }}
                currentUser={currentUser}
              />
            </div>
            {dataTableApiState.inProgress && (
              <SrFlex justify="center">
                <div className="loading-gif" />
              </SrFlex>
            )}
            {shareHouseholdNote && (
              <SrHouseholdNoteShareModal
                listingsApplyingFor={listingsApplyingFor}
                shareHouseholdNote={shareHouseholdNote}
                onClose={() => setShareHouseholdNote(null)}
                onSubmit={(formValues) =>
                  _onSubmit({
                    formValues,
                    reloadItems,
                  })
                }
              />
            )}
          </>
        )}
      </SrDataTableConnected>
    </SrCard>
  );
};

SrAdminHouseholdNotes.propTypes = {
  householdId: PropTypes.number.isRequired,
  listingsApplyingFor: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
};

SrAdminHouseholdNotes.displayName = 'SrAdminHouseholdNotes';

export default SrAdminHouseholdNotes;
